import { Link, Typography } from "@mui/material"
import { StyledEntries } from "./entries.styled"
import { AccountContext } from "@features/tickets/shared/contexts/AccountContext";
import { useContext } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";
import { DataGrid, GridColDef, GridLocaleText, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { dateStringFormatPrinter, padWithZeros } from "@shared/lib/utils";
import ReturnButton from "@shared/ui/navigation/ReturnButton";
import { useTheme } from "@emotion/react";
import { useEntriesBrowse } from "@features/tickets/shared/hooks/useEntriesBrowse";
import { getAccountRoute } from "@features/tickets/shared/utils/accountRoute";
import { Entry } from "@models/entry";


function parseAccountId(value: string | undefined): number | undefined {

  if (!value || !/^\d+$/.test(value)) {
    return undefined;
  }

  const accountId = Number(value);

  return Number.isFinite(accountId) && accountId > 0 ? accountId : undefined;
}


function Entries() {

    const theme = useTheme();
    const { id } = useParams();

    const { account } = useContext(AccountContext);
    const accountId = account?.id ?? parseAccountId(id);

    const {
      data: rows,
      isLoading,
      sortKey,
      sortMethod,
      handleSortKeyChanged,
      handleSortMethodChanged,
      page,
      perPage,
      hasNextPage,
      handleNextPage,
      handlePrevPage,
      handleRowsPerPageChange,
    } = useEntriesBrowse(accountId);

    const customLocaleText: Partial<GridLocaleText> = {

        // Table Content
        noRowsLabel: 'אין רשומות.',

        // Labels
        columnMenuLabel: 'תפריט',
        columnHeaderSortIconLabel: 'מיון',

        // Columns text
        columnMenuUnsort: "ביטול מיון",
        columnMenuSortAsc: "מיון א-ת 9-1",
        columnMenuSortDesc: "מיון ת-א 1-9",
        columnMenuFilter: "סינון",
        columnMenuHideColumn: "הסתר עמודה",
        columnMenuShowColumns: "הצג עמודות",
        columnMenuManageColumns: "ניהול עמודות",

        // Columns Management
        columnsManagementShowHideAllText: "הצג/הסתר הכל",
        columnsManagementReset: "איפוס",
        columnsManagementSearchTitle: "חיפוש",

        // Filter Panel text
        filterPanelOperator: 'פעולה',
        filterPanelOperatorAnd: 'ו-',
        filterPanelOperatorOr: 'או',
        filterPanelColumns: 'עמודה',
        filterPanelInputLabel: 'ערך',
        filterPanelInputPlaceholder: 'ערך לסינון',

        // Filter operators text
        filterOperatorContains: 'מכיל',
        filterOperatorEquals: 'שווה',
        filterOperatorStartsWith: 'מתחיל עם',
        filterOperatorEndsWith: 'נגמר עם',
        filterOperatorIsEmpty: 'הוא ריק',
        filterOperatorIsNotEmpty: 'הוא לא ריק',
        filterOperatorIsAnyOf: 'כל אחד מ',

        filterOperatorIs: 'הוא',
        filterOperatorNot: 'הוא לא',
        filterOperatorAfter: 'הוא אחרי',
        filterOperatorOnOrAfter: 'הוא מ',
        filterOperatorBefore: 'הוא לפני',
        filterOperatorOnOrBefore: 'הוא עד',

        noResultsOverlayLabel: 'לא נמצאו תוצאות.',

        // Pagination
        MuiTablePagination: {
            labelRowsPerPage: 'מס שורות מקסימלי לעמוד:',
            labelDisplayedRows: ({ from, to, count }) =>
                `${from}–${to} מתוך ${count !== -1 ? count : `יותר מ- ${to}`}`,
            backIconButtonProps: {
                sx: { transform: "rotate(180deg)" }
            },
            nextIconButtonProps: {
                sx: { transform: "rotate(180deg)" }
            }
        },
    };


    const columns: GridColDef[] = [
        { field: 'entry_name', headerName: 'תנועה', width: 130 },
        { field: 'created_at', headerName: 'תאריך', type: 'string', valueGetter: (value: string) => dateStringFormatPrinter(value), width: 130 },
        { field: 'document_reference', headerName: 'אסמכתא', width: 130, sortable: false, renderCell: (params) => {
            const row = params.row as Entry;
            const documentId = row.document?.id;
            const label = `${row.document?.document_settings?.short_name || ''} ${padWithZeros(row.document?.document_number, 7)}`;

            if (!documentId) {
              return label;
            }

            return <Link component={RouterLink} to={`/documents/${documentId}`} onClick={(e) => e.stopPropagation()}>{label}</Link>;
        }},
        { field: 'debit', headerName: 'חובה', width: 130, valueGetter: (value: number) => value? value.toFixed(2) +'₪' : ''},
        { field: 'credit', headerName: 'זכות', width: 130 , valueGetter: (value: number) => value? value.toFixed(2) +'₪' : ''},
        { field: 'balance', headerName: 'יתרה', width: 130 , valueGetter: (value: number) => value? value.toFixed(2) +'₪' : ''},
        { field: 'counter_account_id', headerName: 'חן נגדי', width: 130, renderCell: (params) => {
            const row = params.row as Entry;
            const accountId = row.counter_account_id;
            const accountType = row.counter_account_type;
            const label = row.counter_account_number ?? '';

            if (!accountId || !accountType) {
              return label;
            }

            return <Link component={RouterLink} to={`/${getAccountRoute(accountType)}/${accountId}`} onClick={(e) => e.stopPropagation()}>{label}</Link>;
        }},
        { field: 'doc_name_2', headerName: 'אסמכתא-2', width: 130 },
        { field: 'value', headerName: 'ערך', valueGetter: (value: string) => dateStringFormatPrinter(value), width: 200 },
        { field: 'info', headerName: 'פרטים', width: 200 },
    ];

    const rowCount = hasNextPage ? -1 : page * perPage + rows.length;
    const sortModel: GridSortModel = sortKey === "id"
      ? []
      : [{ field: sortKey, sort: sortMethod === "ASC" ? "asc" : "desc" }];

    function handlePaginationModelChange(model: GridPaginationModel) {
      if (model.pageSize !== perPage) {
        handleRowsPerPageChange(model.pageSize);
        return;
      }

      if (model.page > page) {
        handleNextPage();
        return;
      }

      if (model.page < page) {
        handlePrevPage();
      }
    }

    function handleSortModelChange(model: GridSortModel) {
      const sort = model[0];

      if (!sort) {
        handleSortKeyChanged("id");
        handleSortMethodChanged("DESC");
        return;
      }

      handleSortKeyChanged(sort.field);
      handleSortMethodChanged(sort.sort === "asc" ? "ASC" : "DESC");
    }

    if (!accountId) {
      return (
        <StyledEntries>
          <div className="container">
            <Typography variant="body1">לא נמצא חשבון.</Typography>
            <div className="buttons">
              <ReturnButton />
            </div>
          </div>
        </StyledEntries>
      );
    }

    return (
        <StyledEntries>
            <div className="container">
                <div className="top">
                    <div>
                        <Typography variant="body1" component={"span"}>מספר חשבון:</Typography>
                        <Typography variant="body2" component={"span"}>{account?.account_number}</Typography>
                    </div>
                    <div>
                        <Typography variant="body1" component={"span"}>שם חשבון:</Typography>
                        <Typography variant="body2" component={"span"}>{account?.account_name}</Typography>
                    </div>
                </div>
                <DataGrid
                    disableRowSelectionOnClick
                    disableColumnFilter
                    columns={columns}
                    rows={rows}
                    autoHeight
                    loading={isLoading}
                    paginationMode="server"
                    sortingMode="server"
                    rowCount={rowCount}
                    paginationMeta={{ hasNextPage }}
                    paginationModel={{ page, pageSize: perPage }}
                    onPaginationModelChange={handlePaginationModelChange}
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    localeText={customLocaleText}
                    sx={{
                        '& .MuiDataGrid-topContainer svg': {
                            color: "rgba(0,0,0,0.57)",
                        },
                        '& .MuiDataGrid-virtualScrollerContent, .MuiTablePagination-root, .MuiSvgIcon-root': {
                            color: theme.colors.main_color,

                        }
                    }}
                />
                <div className="buttons">
                    <ReturnButton/>
                </div>
            </div>
        </StyledEntries>
    )

}

export default Entries
